import React from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingBag, Trash2 } from "lucide-react";
import { SEO } from "@/components/common/SEO";
import { Breadcrumbs } from "@/components/common/Breadcrumbs";
import { useWishlist } from "@/context/WishlistContext";
import { useCart } from "@/context/CartContext";
import { formatINR } from "@/lib/format";

export default function WishlistPage() {
  const { items, remove } = useWishlist();
  const { addItem } = useCart();

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
      <SEO title="Wishlist" />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Wishlist" }]} />
      <h1 className="mt-6 font-serif text-4xl font-light text-ink sm:text-5xl">My Wishlist</h1>
      <p className="mt-2 text-sm text-muted-foreground">{items.length} {items.length === 1 ? "piece" : "pieces"} saved</p>

      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="wishlist-empty">
          <Heart className="h-12 w-12 text-gold" strokeWidth={1.2} />
          <p className="mt-5 font-serif text-2xl text-ink">Your wishlist is empty</p>
          <p className="mt-2 text-sm text-muted-foreground">Tap the heart on any piece to save it here.</p>
          <Link to="/shop" className="mt-6 bg-ink px-10 py-4 text-[11px] uppercase tracking-[0.2em] text-ivory hover:bg-gold">
            Discover Jewellery
          </Link>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-2 gap-x-5 gap-y-10 sm:gap-x-8 lg:grid-cols-4" data-testid="wishlist-grid">
          {items.map((p) => (
            <div key={p.handle} className="group flex flex-col" data-testid="wishlist-item">
              <div className="relative aspect-[4/5] overflow-hidden bg-cream">
                <Link to={`/product/${p.handle}`}>
                  <img src={p.images?.[0]} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </Link>
                <button onClick={() => remove(p.handle)} aria-label="Remove" className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-white/85 text-ink backdrop-blur-sm hover:text-destructive">
                  <Trash2 size={16} />
                </button>
              </div>
              <div className="pt-4">
                <p className="text-luxe text-[10px] text-gold">{p.category}</p>
                <Link to={`/product/${p.handle}`}>
                  <h3 className="mt-1 font-serif text-lg text-ink hover:text-gold">{p.title}</h3>
                </Link>
                <p className="mt-1 text-sm font-semibold text-ink">{formatINR(p.price)}</p>
                <button
                  onClick={() => addItem(p, p.variants?.[0]?.id, 1)}
                  disabled={!p.available}
                  className="mt-3 flex w-full items-center justify-center gap-2 border border-ink py-3 text-[11px] uppercase tracking-[0.15em] text-ink transition-colors hover:bg-ink hover:text-ivory disabled:opacity-50"
                >
                  <ShoppingBag size={14} /> {p.available ? "Move to Bag" : "Sold Out"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
